import { useTranslation } from 'react-i18next';
import { CalendarClock, X } from 'lucide-react';
import type { DeviceDto } from '@gc/shared';

import { bannerItems, timeOfDay } from './bookingLogic';

interface ReservationAlertsBannerProps {
  devices: readonly DeviceDto[];
  now: number;
  dismissedBookingIds: ReadonlySet<string>;
  onDismiss: (bookingId: string) => void;
}

/**
 * The ≤15-min/overdue strip above the Device List (WAVE2-DESIGN §1). Rendered
 * at all only when `enableReservationAlerts` is on — the caller holds that
 * gate — and dismissal is per booking, so silencing one party's row does not
 * silence the next.
 */
export function ReservationAlertsBanner({
  devices,
  now,
  dismissedBookingIds,
  onDismiss,
}: ReservationAlertsBannerProps) {
  const { t } = useTranslation();
  const items = bannerItems(devices, now, dismissedBookingIds);
  if (items.length === 0) return null;

  return (
    <div role="status" className="mx-4 mb-2 flex flex-col gap-1">
      {items.map((item) => (
        <div
          key={item.booking.id}
          data-attention={item.attention}
          className={[
            'flex items-center gap-2 rounded-control px-3 py-1.5 text-sm',
            item.attention === 'overdue' ? 'bg-danger/15 text-danger' : 'bg-ending/15 text-content',
          ].join(' ')}
        >
          <CalendarClock className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {t(
              item.attention === 'overdue' ? 'bookings.banner.overdue' : 'bookings.banner.upcoming',
              {
                device: item.deviceName,
                name: item.booking.partyName,
                time: timeOfDay(item.booking.startsAt),
              },
            )}
          </span>
          <button
            type="button"
            onClick={() => onDismiss(item.booking.id)}
            aria-label={t('bookings.banner.dismiss')}
            title={t('bookings.banner.dismiss')}
            className="rounded-full p-0.5 hover:bg-hover"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
